/* NINJA TYO — ambient wind
   Leaves carried on a real wind field, with gusts.

   ---------------------------------------------------------------------
   TUNED 2026-07-29 on Guto's note: "less leaves, wind lines cut by half
   and more subtle, thinner, a little too much distraction. Wind physics is
   good — could be a little stronger, blow them away further."

   So: FEWER things on screen and QUIETER streaks, but MORE force. The
   scene should read as weather you notice once, not weather that competes
   with the product photography.

     particle count   48 max / W/42  ->  24 max / W/84   (halved)
     streak cap       14 / 5         ->  7 / 2           (halved)
     streak spawn     0.022          ->  0.011           (halved)
     streak thickness 0.7-2.2px      ->  0.3-0.9px       (thinner)
     streak brightness 0.34+0.11*sq  ->  0.14+0.05*sq    (subtler)
     halo opacity     a*0.34         ->  a*0.18
     BASE breeze      -0.52          ->  -0.62           (stronger)
     SQUALL_PEAK      16.0           ->  21.0            (blows further)

   ⚠ Two constraints that must survive any future retune:
     1. GUSTS MULTIPLY THE BREEZE, THEY NEVER ADD TO IT. An additive term
        big enough to feel like a gust can out-weigh BASE and flip vx
        positive — the wind visibly reverses mid-gust, which no wind does.
     2. GRAVITY IS SIZED AGAINST THE DRAG, not chosen in isolation. Drag
        pulls vy toward the air's vertical speed (~0), so raising drag
        without raising g leaves leaves HOVERING instead of falling.
   ---------------------------------------------------------------------

   Free by design: skipped under prefers-reduced-motion, paused when the
   tab is hidden, particle count scaled to viewport, pointer-events none,
   aria-hidden. Loaded ONLY on the homepage and About — see nt-head.liquid.
*/
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'nt-wind';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.prepend(canvas);
  const ctx = canvas.getContext('2d', { alpha: true });

  let W, H, dpr, particles, gusts, raf = null, last = 0, t = 0;

  /* A SQUALL every ~10s: a short, much stronger blast on top of the breeze.
     Ramps in and out on a bell curve rather than switching on, because air
     has momentum — a hard step looks like a bug, not weather. */
  const SQUALL_PEAK = 21.0;         // was 16.0 — blows leaves further
  let squallAt = 0, squallStart = -1, squallDur = 0;

  const rand = (a, b) => a + Math.random() * (b - a);

  /* Leaf tints, all inside the brand's teal range so the weather never
     fights the palette. Paler ones read as further away. */
  const TINTS = [
    ['#5fc4da', '#2e8ea6'],
    ['#7ad8e8', '#3f9fb5'],
    ['#3fa8c0', '#1f7288'],
    ['#9ee6f0', '#59b2c4'],
    ['#d8f0f6', '#8bc4d2'],
  ];

  function squallFactor(time) {
    if (squallStart < 0) {
      if (time >= squallAt) {
        squallStart = time;
        squallDur = rand(2900, 3150);       // ~3s of wind
      }
      return 1;
    }
    const k = (time - squallStart) / squallDur;
    if (k >= 1) {
      squallStart = -1;
      squallAt = time + rand(6300, 7700);   // ~10s start-to-start
      return 1;
    }
    // rises quickly, falls away slowly, the way a real gust front passes
    const env = Math.sin(Math.PI * Math.pow(k, 0.62));
    return 1 + env * (SQUALL_PEAK - 1);
  }

  /* Wind field. Base breeze plus three out-of-phase sines whose periods are
     mutually irrational, so gusts never land on a predictable beat.
     Calm is a slow FALL with a hint of sideways drift; the squall is the
     event. Keeping BASE low is what gives the blast something to contrast
     against. */
  const BASE = -0.62;               // was -0.52 — a touch stronger
  function windAt(y, time) {
    const g =
      Math.sin(time * 0.00042) * 0.55 +
      Math.sin(time * 0.00097 + 1.7) * 0.30 +
      Math.sin(time * 0.00231 + 4.1) * 0.15;
    // shear: air moves faster higher up, as it does over open ground
    const shear = 1 - (y / H) * 0.35;
    const gustFactor = 1 + g * 0.85;        // 0.15x .. 1.85x, never negative
    const squall = squallFactor(time);
    // a gust front lifts as well as pushes
    const lift = (squall - 1) * 0.055;
    return {
      vx: BASE * gustFactor * shear * squall,
      vy: 0.10 + Math.abs(g) * 0.05 - lift,
      squall,
    };
  }

  function resize() {
    dpr = Math.min(devicePixelRatio || 1, 2);
    W = innerWidth; H = innerHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // HALVED: was min(48, max(16, W/42)). ~17 leaves at 1440px, was ~34.
    const target = Math.round(Math.min(24, Math.max(8, W / 84)));
    particles = Array.from({ length: target }, () => spawn(true));
  }

  function spawn(anywhere) {
    const leaf = Math.random() < 0.72;
    const size = leaf ? rand(13, 26) : rand(7, 13);
    return {
      x: anywhere ? rand(0, W) : rand(W + 20, W + 260),
      y: anywhere ? rand(0, H) : rand(-60, H * 0.85),
      size, leaf,
      vx: rand(-1.1, -0.3),
      vy: rand(0.25, 0.75),
      // bigger things carry more inertia and answer the wind more slowly
      drag: leaf ? rand(0.11, 0.19) : rand(0.19, 0.30),
      mass: leaf ? rand(0.9, 1.5) : rand(0.4, 0.8),
      rot: rand(0, Math.PI * 2),
      rotV: 0,
      rest: rand(0, Math.PI * 2),
      spinDir: Math.random() < 0.5 ? -1 : 1,
      wobFreq: rand(0.6, 1.9),
      wobPhase: rand(0, Math.PI * 2),
      flutter: rand(0.5, 1.5),
      phase: rand(0, Math.PI * 2),
      alpha: leaf ? rand(0.45, 0.85) : rand(0.35, 0.7),
      tint: TINTS[(Math.random() * TINTS.length) | 0],
      belly: rand(0.54, 0.76),
    };
  }

  /* A leaf: two curves meeting at base and tip, with a stem.
     Everything is FILLED, never stroked — these sprites are squashed on one
     axis as they tumble, and a stroke under non-uniform scale distorts into
     long tails that looked like glitches trailing each leaf. */
  function drawLeaf(ctx, r, tint, belly, big) {
    if (big) {
      ctx.beginPath();
      ctx.moveTo(-r * 0.98, -r * 0.045);
      ctx.lineTo(-r * 1.34, r * 0.10);
      ctx.lineTo(-r * 0.98, r * 0.055);
      ctx.closePath();
      ctx.fillStyle = tint[1];
      ctx.fill();
    }
    ctx.beginPath();
    ctx.moveTo(-r, 0);
    ctx.quadraticCurveTo(-r * 0.05, -r * belly, r, 0);
    ctx.quadraticCurveTo(-r * 0.05, r * belly * 0.80, -r, 0);
    ctx.closePath();
    ctx.fillStyle = tint[0];
    ctx.fill();

    if (big && r > 14) {
      ctx.beginPath();
      ctx.moveTo(-r * 0.86, 0);
      ctx.quadraticCurveTo(0, -r * belly * 0.16, r * 0.86, 0);
      ctx.quadraticCurveTo(0, -r * belly * 0.05, -r * 0.86, 0);
      ctx.closePath();
      ctx.fillStyle = tint[1];
      ctx.globalAlpha *= 0.75;
      ctx.fill();
      ctx.globalAlpha /= 0.75;
    }
  }

  function spawnGust(squall = 1) {
    gusts.push({
      x: W + 240, y: rand(H * 0.06, H * 0.94),
      len: rand(430, 1010) * (0.8 + squall * 0.18),
      speed: rand(9, 16) * Math.max(1, squall * 0.55),
      life: 1,
      thick: rand(0.3, 0.9),                       // was 0.7-2.2 — thinner
      lit: Math.min(0.42, 0.14 + 0.05 * squall),   // was 0.34 + 0.11*sq
    });
  }

  function step(now) {
    const dt = Math.min(3, (now - last) / 16.6667);
    last = now; t = now;
    draw(dt);
    raf = requestAnimationFrame(step);
  }

  function draw(dt = 1) {
    ctx.clearRect(0, 0, W, H);

    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      const air = windAt(p.y, t);

      /* RELATIVE AIRFLOW is what a leaf actually feels. Everything below
         scales off it, which is why the same leaf drifts in calm and goes
         berserk in a gust with no special-casing. */
      const rx = air.vx - p.vx, ry = air.vy - p.vy;
      const rel = Math.hypot(rx, ry);

      const k = Math.min(0.72, p.drag * (0.9 + rel * 0.7));
      p.vx += rx * k * dt;
      p.vy += ry * k * dt;

      p.vy += 0.26 * p.mass * dt;      // gravity — sized against drag

      /* ROTATION — a damped pendulum, not a spin. A leaf hanging in still
         air ROCKS about a rest angle; only airflow strong enough to beat
         that restoring torque tumbles it over. */
      const restore = -Math.sin(p.rot - p.rest) * 0.020;
      const buffet =
        (Math.sin(t * 0.0026 * p.wobFreq + p.wobPhase) * 0.7 +
         Math.sin(t * 0.0011 * p.wobFreq + p.wobPhase * 1.7) * 0.3) *
        rel * 0.014 * p.spinDir;
      p.rotV += (restore + buffet) * dt;
      p.rotV *= 1 - 0.055 * dt;
      p.rotV = Math.max(-0.26, Math.min(0.26, p.rotV));
      p.rot += p.rotV * dt;

      /* AUTOROTATION LIFT. A tumbling leaf throws lift sideways to its
         travel, flipping direction as it turns over. That alternating
         side-force is why real leaves loop, dart and climb instead of
         sliding along the wind. */
      const sp = Math.hypot(p.vx, p.vy) || 0.001;
      const nx = -p.vy / sp, ny = p.vx / sp;
      const lift = Math.sin(p.rot * 2) * rel * 0.22 * p.flutter;
      p.vx += nx * lift * dt;
      p.vy += ny * lift * dt;

      /* TURBULENCE — a gust front is full of eddies, not one slab. */
      const swirl = air.squall - 0.4;
      p.vx += Math.sin(p.y * 0.013 + t * 0.0017) * 0.16 * swirl * dt;
      p.vy += Math.cos(p.x * 0.010 - t * 0.0021) * 0.14 * swirl * dt;

      // ceiling raised with SQUALL_PEAK so the stronger blast is not clipped
      const vmag = Math.hypot(p.vx, p.vy);
      if (vmag > 42) { p.vx = p.vx / vmag * 42; p.vy = p.vy / vmag * 42; }

      p.phase += (0.05 + rel * 0.05) * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      if (p.x < -90 || p.x > W + 260 || p.y > H + 90 || p.y < -220)
        particles[i] = spawn(false);

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.scale(1, Math.max(0.46, Math.abs(Math.cos(p.rot))));
      ctx.globalAlpha = p.alpha;
      drawLeaf(ctx, p.size, p.tint, p.belly, p.leaf);
      ctx.restore();
    }

    for (let i = gusts.length - 1; i >= 0; i--) {
      const g = gusts[i];
      g.x -= g.speed * dt;
      g.life -= 0.010 * dt;
      if (g.x + g.len < -60 || g.life <= 0) { gusts.splice(i, 1); continue; }
      const x2 = g.x + g.len, y2 = g.y - g.len * 0.10;
      const a = (g.lit ?? 0.3) * g.life;
      /* Still drawn twice — a wide soft halo, then a narrow core. One flat
         stroke reads as a scratch on the screen; two read as moving air.
         Both are dimmer and thinner than before. */
      const halo = ctx.createLinearGradient(g.x, g.y, x2, y2);
      halo.addColorStop(0, 'rgba(180,225,238,0)');
      halo.addColorStop(0.5, `rgba(180,225,238,${a * 0.18})`);
      halo.addColorStop(1, 'rgba(180,225,238,0)');
      ctx.strokeStyle = halo;
      ctx.lineWidth = g.thick * 2.8;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(g.x, g.y); ctx.lineTo(x2, y2); ctx.stroke();

      const core = ctx.createLinearGradient(g.x, g.y, x2, y2);
      core.addColorStop(0, 'rgba(236,250,255,0)');
      core.addColorStop(0.45, `rgba(236,250,255,${a})`);
      core.addColorStop(0.7, `rgba(236,250,255,${a * 0.8})`);
      core.addColorStop(1, 'rgba(236,250,255,0)');
      ctx.strokeStyle = core;
      ctx.lineWidth = g.thick;
      ctx.beginPath(); ctx.moveTo(g.x, g.y); ctx.lineTo(x2, y2); ctx.stroke();
    }

    // HALVED: spawn rate 0.022 -> 0.011, caps 14/5 -> 7/2
    const air = windAt(H / 2, t);
    const strength = Math.abs(air.vx);
    const cap = air.squall > 1.5 ? 7 : 2;
    if (Math.random() < 0.011 * strength * dt && gusts.length < cap) {
      spawnGust(air.squall);
    }
  }

  function start() {
    if (raf) return;
    last = performance.now(); t = last;
    if (!squallAt) squallAt = t + rand(4000, 7000);
    draw();
    raf = requestAnimationFrame(step);
  }
  function stop() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

  gusts = [];
  resize();
  addEventListener('resize', resize, { passive: true });
  document.addEventListener('visibilitychange',
    () => (document.hidden ? stop() : start()));
  reduce.addEventListener('change', e => {
    if (e.matches) { stop(); canvas.remove(); } else start();
  });
  start();
})();
